import {
    IResponseBuffer,
    IResponseStream
} from '~/packages/ai-core/types/internal-types';
import { ResponseTimeoutError } from '~/packages/ai-core/types/public-types';
import { AIResponse } from '~/packages/ai-core/types/server-types';

/**
 * Implements IResponseStream to provide a stream of responses with timeout handling
 */
export class ResponseStream implements IResponseStream {
    private readonly buffer: IResponseBuffer;
    private readonly timeoutMs: number;

    constructor(buffer: IResponseBuffer, timeoutMs: number = 500) {
        this.buffer = buffer;
        this.timeoutMs = timeoutMs;
    }

    async *responses(): AsyncGenerator<AIResponse, void, unknown> {
        const iterator = this.buffer.responses();

        while (true) {
            let timeoutReference;
            const timeoutPromise = new Promise<never>((_, reject) => {
                timeoutReference = setTimeout(() => {
                    reject(
                        new ResponseTimeoutError(
                            `No response received within ${this.timeoutMs}ms`
                        )
                    );
                }, this.timeoutMs);
            });
            const nextResponse = await Promise.race([
                iterator.next(),
                timeoutPromise
            ]);
            // Not critical because promises rejected after .race is settled are ignored,
            // but we still want to clear timeout to release resources
            clearTimeout(timeoutReference);

            if (nextResponse.done) {
                return;
            }

            if (nextResponse.value) {
                yield nextResponse.value;
            } else {
                throw new Error('Missing response value');
            }
        }
    }
}
